import { Component, ElementRef, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { DEFAULT_FONT_FAMILY, DEFAULT_LIGHT_THEME_TEXT_COLOR } from '@app-core/constants/constants';
import { Chart } from 'chart.js';
import { EVENTS_CONFIG } from '@app-core/constants/constants';
import { TranslateService } from '@ngx-translate/core';
import { DataService } from '@app-core/services/data/data.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-behaviour-summary',
  templateUrl: './behaviour-summary.component.html',
  styleUrls: ['./behaviour-summary.component.scss'],
})
export class BehaviourSummaryComponent implements OnInit, OnChanges {
  @Input() public coachingDetails = [];
  @Input() public uniqueCoachedEvents = [];
  @Input() public eventLists = {};
  @Input() public loader: boolean = false;

  public eventsConfig = EVENTS_CONFIG;
  public horizontalBarChart: Chart | undefined; // Define the type of your chart
  public sortedEventList = [];
  public showChart: boolean = false;

  private chartLabels: string[] = [];
  private chartData: number[] = [];
  private ngUnsubscribe: Subject<void> = new Subject<void>();

  constructor(private elementRef: ElementRef, public translate: TranslateService, public dataService: DataService) {}

  public ngOnInit(): void {
    this.createChart();
    this.dataService._currentLanguage.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value: string) => {
      if (value) {
        this.changeLanguage();
      }
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.eventLists && changes.eventLists?.currentValue && this.eventLists) {
      this.sortedEventList = Object.entries(this.eventLists)
        .filter(([key, value]) => key !== 'total' && value !== 0)
        .map(([key, value]) => ({
          event: this.getEventName(key),
          count: value,
          colour: this.getBarColour(key),
          hoverColour: this.getHoverColour(key),
        }));
      this.createChart();
    }
  }

  private getBarColour(eventType: string) {
    if (this.uniqueCoachedEvents.includes(eventType)) {
      return '#9A66FF';
    } else {
      return '#E6DCFB';
    }
  }

  private getHoverColour(eventType: string) {
    if (this.uniqueCoachedEvents.includes(eventType)) {
      return '#7951C6';
    } else {
      return '#C5B8DF';
    }
  }

  private getEventName(eventType: string) {
    return this.eventsConfig[eventType].label;
  }

  private createChart() {
    if (!this.sortedEventList.length) {
      this.showChart = false;
      return;
    }
    if (this.horizontalBarChart) {
      this.horizontalBarChart.destroy(); // Destroy the existing chart if it exists
    }
    this.showChart = true;
    this.chartLabels = this.sortedEventList.map((obj) => this.translate.instant(obj.event));
    this.chartData = this.sortedEventList.map((obj) => obj.count);
    const backgroundColor = this.sortedEventList.map((obj) => obj.colour);
    const borderColor = this.sortedEventList.map((obj) => obj.colour);
    const hoverColour = this.sortedEventList.map((obj) => obj.hoverColour);

    const data = {
      labels: this.chartLabels,
      datasets: [
        {
          data: this.chartData,
          backgroundColor: backgroundColor,
          borderColor: borderColor,
          borderWidth: 0.5,
          borderRadius: 5,
          barPercentage: 0.3,
          hoverBackgroundColor: hoverColour,
          pointStyle: 'circle',
        },
      ],
    };
    this.horizontalBarChart = new Chart(this.elementRef.nativeElement.querySelector('#coachingReportChart'), {
      type: 'bar',
      data: data,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            grid: {
              display: false,
            },
            title: {
              display: true,
              text: this.translate.instant('EVENT CATEGORY'),
              font: {
                family: DEFAULT_FONT_FAMILY,
                size: 12,
                lineHeight: 2.4,
              },
              color: DEFAULT_LIGHT_THEME_TEXT_COLOR,
            },
          },
          y: {
            grid: {
              display: true,
            },
            title: {
              display: true,
              text: this.translate.instant('EVENT COUNT'),
              font: {
                family: DEFAULT_FONT_FAMILY,
                size: 12,
                lineHeight: 2.4,
              },
              color: DEFAULT_LIGHT_THEME_TEXT_COLOR,
            },
          },
        },

        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            boxWidth: 10,
            boxHeight: 10,
            boxPadding: 4,
            cornerRadius: 4,
            usePointStyle: true,
            bodyFont: {
              family: DEFAULT_FONT_FAMILY,
            },
          },
        },
      },
    });
  }

  private changeLanguage() {
    if (this.horizontalBarChart) {
      this.horizontalBarChart.data.labels = this.sortedEventList.map((obj) => this.translate.instant(obj.event));
      if ((this.horizontalBarChart.options.scales.y as any).title) {
        ((this.horizontalBarChart.options.scales.y as any).title as any).text = this.translate.instant('EVENT COUNT');
      }
      if ((this.horizontalBarChart.options.scales.x as any).title) {
        ((this.horizontalBarChart.options.scales.x as any).title as any).text = this.translate.instant('EVENT CATEGORY');
      }
      this.horizontalBarChart.update();
    }
  }
}
